import { MathUtils } from 'three';
import { cloudPointerMotion, type CloudPointer } from './cloudPointer';
import type { CloudInteractionState } from './skyStore';

export const CLOUD_TRAIL_CAPACITY = 16;

interface TrailPoint {
  x: number;
  y: number;
  time: number;
  radius: number;
  strength: number;
}

interface TrailSegment {
  start: TrailPoint;
  end: TrailPoint;
}

interface CloudTrail {
  segments: TrailSegment[];
  radius: number;
  strength: number;
  previous: TrailPoint | null;
  stroke: number;
}

export const createCloudTrail = (): CloudTrail => ({
  segments: [],
  radius: 0,
  strength: 0,
  previous: null,
  stroke: -1,
});

export const trailFade = (
  time: number,
  now: number,
  duration: number
): number => 1 - MathUtils.smoothstep(now - time, 0, duration * 1000);

export const updateCloudTrail = (
  trail: CloudTrail,
  pointer: CloudPointer,
  settings: CloudInteractionState,
  now: number,
  delta: number
): void => {
  if (!settings.enabled) {
    trail.segments.length = 0;
    trail.previous = null;
    trail.strength = 0;
    trail.radius = 0;
    return;
  }
  while (
    trail.segments.length &&
    trailFade(trail.segments[0].end.time, now, settings.trailDuration) === 0
  ) {
    trail.segments.shift();
  }
  const speedStrength = MathUtils.smoothstep(
    pointer.speed,
    0,
    settings.fullStrengthSpeed
  );
  const target = speedStrength * cloudPointerMotion(pointer, now);
  const targetRadius = MathUtils.lerp(
    Math.min(settings.minRadius, settings.radius),
    settings.radius,
    target
  );
  trail.radius = MathUtils.damp(
    trail.radius,
    targetRadius,
    1 / Math.max(0.01, settings.radiusEasing),
    Math.min(delta, 0.1)
  );
  trail.strength = MathUtils.damp(
    trail.strength,
    target,
    12,
    Math.min(delta, 0.1)
  );
  if (!pointer.active) {
    trail.previous = null;
    return;
  }
  if (
    !pointer.movedAt ||
    pointer.movedAt === trail.previous?.time ||
    now - pointer.movedAt > 180
  )
    return;

  const point: TrailPoint = {
    x: pointer.x,
    y: pointer.y,
    time: pointer.movedAt,
    radius: trail.radius,
    strength: trail.strength,
  };
  const connected =
    trail.previous &&
    trail.stroke === pointer.stroke &&
    pointer.movedAt - trail.previous.time < 180;
  const last = trail.segments.at(-1);
  const spacing = (settings.trailDuration * 1000) / (CLOUD_TRAIL_CAPACITY - 2);
  if (connected && last && point.time - last.start.time < spacing) {
    last.end = point;
  } else {
    trail.segments.push({
      start: connected && trail.previous ? trail.previous : point,
      end: point,
    });
    if (trail.segments.length > CLOUD_TRAIL_CAPACITY) trail.segments.shift();
  }
  trail.previous = point;
  trail.stroke = pointer.stroke;
};
